"use client";

import { generateReactHelpers } from "@uploadthing/react";
import { OurFileRouter } from "@/app/api/uploadthing/core";
import { AnexoFinanceiroIdentificador } from "./_types";
import { buildAnexoPrefix } from "./helpers";
import { uploadAnexos } from "@/_modulos/prenota/inclusao";
import { useUploadAnexoStore } from "@/_modulos/prenota/inclusao/f-salvar/a-anexo/_state";

const { uploadFiles } = generateReactHelpers<OurFileRouter>();

/**
 * HOOK DE TESTE PARA UPLOAD DE ARQUIVOS
 * 
 * COMO FUNCIONA ATUALMENTE:
 * 1. Recebe arquivos PDF do usuário
 * 2. Gera dados aleatórios para simular um documento financeiro:
 *    - Filial: "0101" (fixo)
 *    - Loja: "01" (fixo) 
 *    - Fornecedor: número aleatório de 6 dígitos (100000-999999)
 *    - Documento: número aleatório de 6 dígitos (100000-999999)
 * 3. Cria um identificador único baseado nesses dados
 * 4. Gera um prefixo padronizado para o nome do arquivo
 * 5. Renomeia os arquivos com o prefixo + sequência (01, 02, etc.)
 * 6. Faz upload via UploadThing para o servidor atual
 * 
 * EXEMPLO DE NOME GERADO:
 * - Prefixo: "0101_01_123456_789012"
 * - Arquivo: "0101_01_123456_789012_01.pdf"
 */

export const useTestUpload = () => {
  const testUpload = async (arquivos: File[], tipo?: string) => {
    if (!arquivos.length) throw new Error("Nenhum arquivo para upload");

    try {
      // Gera dados aleatórios para teste
      const filial = "0101";
      const loja = "01";
      const fornecedor = Math.floor(Math.random() * 900000) + 100000; // 100000-999999 //6 caracteres
      const doc = Math.floor(Math.random() * 900000) + 100000; // 100000000-999999999 //6 caracteres
      
      const identificador: AnexoFinanceiroIdentificador = {
        filial,
        fornecedor: String(fornecedor),
        loja,
        cod: String(doc),
      };

      const prefix = buildAnexoPrefix(identificador);
      console.log("🧪 [TESTE] Identificador gerado:", identificador);
      console.log("🧪 [TESTE] Prefixo gerado:", prefix);

      const filesToUpload = arquivos.map((file, index) => {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "pdf";
        
        // Valida se é um arquivo PDF
        if (ext !== "pdf") {
          throw new Error(`Apenas arquivos PDF são permitidos. Arquivo recebido: ${file.name}`);
        }
        
        // Usa o prefixo correto baseado no identificador
        const seq = String(index + 1).padStart(2, "0"); 
        const newName = `${prefix}_${seq}.${ext}`;
        
        // Cria um novo File com o mesmo conteúdo, mas com nome padronizado
        return new File([file], newName, {
          type: "application/pdf", // Força o tipo correto
          lastModified: file.lastModified,
        });
      });

      console.log("🧪 [TESTE] Arquivos para upload:", filesToUpload.map(f => f.name));

      // UPLOAD ATUAL: Via UploadThing para servidor local/atual
      const results = await uploadFiles("anexoUploader", { files: filesToUpload });
      return results;
    } catch (err) {
      console.error("Erro no upload:", err);
      throw err;
    }
  };

  return { testUpload };
};

/**
 * FUNÇÃO PARA UPLOAD DIRETO USANDO uploadAnexos
 * 
 * Esta função usa a função uploadAnexos que faz upload para o servidor real
 * através da API /api/apps/upload (que vai para 172.16.99.182:3001)
 */
export const useDirectUpload = () => {
  const directUpload = async (arquivos: File[], tipo?: string) => {
    if (!arquivos.length) throw new Error("Nenhum arquivo para upload");

    try {
      // Gera dados aleatórios para teste (mesmo processo)
      const filial = "0101";
      const loja = "01";
      const fornecedor = Math.floor(Math.random() * 900000) + 100000;
      const doc = Math.floor(Math.random() * 900000) + 100000;
      
      const identificador: AnexoFinanceiroIdentificador = {
        filial,
        fornecedor: String(fornecedor),
        loja,
        cod: String(doc),
      };

      const prefix = buildAnexoPrefix(identificador);
      console.log("🚀 [DIRETO] Identificador gerado:", identificador);
      console.log("🚀 [DIRETO] Prefixo gerado:", prefix);

      // Prepara os arquivos com nomes padronizados
      const filesToUpload = arquivos.map((file, index) => {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "pdf";
        
        if (ext !== "pdf") {
          throw new Error(`Apenas arquivos PDF são permitidos. Arquivo recebido: ${file.name}`);
        }
        
        const seq = String(index + 1).padStart(2, "0"); 
        const newName = `${prefix}_${seq}.${ext}`;
        
        return new File([file], newName, {
          type: "application/pdf",
          lastModified: file.lastModified,
        });
      });

      console.log("🚀 [DIRETO] Arquivos para upload:", filesToUpload.map(f => f.name));

      // Limpa a store de uploads antes de adicionar novos arquivos
      const store = useUploadAnexoStore.getState();
      store.clearUploads();

      // Adiciona os arquivos na store de uploads (necessário para uploadAnexos funcionar)
      filesToUpload.forEach((file, index) => {
        const seq = String(index + 1).padStart(3, "0"); // 001, 002, 003...
        store.addUpload({
          seq,
          doc: String(doc), // Usa o documento gerado
          file,
          tipo: tipo || "financeiro"
        });
      });

      console.log("🚀 [DIRETO] Arquivos adicionados na store:", store.uploads.length);

      // Log detalhado dos uploads na store
      store.uploads.forEach((upload, index) => {
        console.log(`🚀 [DIRETO] Upload ${index + 1}:`, {
          seq: upload.seq,
          doc: upload.doc,
          fileName: upload.file?.name,
          fileSize: upload.file?.size,
          tipo: upload.tipo
        });
      });

      // Usa uploadAnexos para fazer o upload real
      const results = await uploadAnexos(String(doc));

      console.log("🚀 [DIRETO] Resultado do uploadAnexos:", results);

      // Verifica se houve erros
      const erros = results.filter(r => r.status === "erro");
      const sucessos = results.filter(r => r.status === "sucesso");

      if (erros.length > 0) {
        console.error("🚀 [DIRETO] Erros encontrados:", erros);
        throw new Error(`${erros.length} arquivo(s) falharam no upload`);
      }

      console.log(`🚀 [DIRETO] Upload concluído: ${sucessos.length} sucesso(s)`);
      return {
        success: true,
        results,
        sucessos: sucessos.length,
        erros: erros.length
      };

    } catch (err) {
      console.error("🚀 [DIRETO] Erro no upload direto:", err);
      throw err;
    }
  };

  return { directUpload };
};
